/* eslint-disable react/prop-types */
/**
 * LinkExistingClaimDialog — extracted VERBATIM from App.js (Jun 2026,
 * Prepare-Claim V2 migration).  Recovery dialog for orphaned notes whose
 * claim_id is null but a real claim exists.  Self-contained: fetches its
 * own candidates on open.  Used by BOTH the legacy dashboard and
 * PrepareClaimHostV2.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { DebouncedInput } from '../ui/debounced-textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LinkExistingClaimDialog = ({ open, onOpenChange, noteId, onLinked }) => {
  const [candidateClaims, setCandidateClaims] = useState([]);
  const [candidateClaimsLoading, setCandidateClaimsLoading] = useState(false);
  const [manualClaimNumber, setManualClaimNumber] = useState('');
  const [linkingClaimId, setLinkingClaimId] = useState(null);

  const linkClaim = async (payload, label) => {
    try {
      setLinkingClaimId(payload.claim_id || 'manual');
      await axios.post(`${API}/billing/notes/${noteId}/link-existing-claim`, payload);
      toast.success(`Linked to ${label}`);
      onLinked && onLinked(noteId);
    } catch (e) {
      toast.error(`Link failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLinkingClaimId(null);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (o && noteId) {
          // Fetch candidates fresh each open so newly-created claims are eligible.
          setCandidateClaimsLoading(true);
          setCandidateClaims([]);
          setManualClaimNumber('');
          axios
            .get(`${API}/billing/notes/${noteId}/candidate-claims`)
            .then(r => setCandidateClaims(r.data?.candidate_claims || []))
            .catch(e => toast.error(`Failed to load candidate claims: ${e.response?.data?.detail || e.message}`))
            .finally(() => setCandidateClaimsLoading(false));
        }
      }}
    >
      <DialogContent className="max-w-3xl" data-testid="link-existing-claim-dialog">
        <DialogHeader>
          <DialogTitle>Link Note to Existing Claim</DialogTitle>
          <DialogDescription>
            This note doesn't have a claim attached, but a claim may already exist in the system.
            Pick the matching claim below — re-linking prevents duplicate claims and removes the
            note from "Notes Ready for Billing".
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {candidateClaimsLoading && (
            <div className="text-sm text-gray-500" data-testid="link-existing-claim-loading">Loading candidate claims…</div>
          )}

          {!candidateClaimsLoading && candidateClaims.length > 0 && (
            <div className="border rounded-md divide-y" data-testid="link-existing-claim-candidates">
              {candidateClaims.map((c) => {
                const matchLabel =
                  c.match_score >= 100 ? 'Exact match (note ID)'
                  : c.match_score >= 50 ? 'Likely match (appointment)'
                  : c.match_score >= 25 ? 'Likely match (client + DOS)'
                  : 'Possible match';
                return (
                  <div key={c.id} className="p-3 flex items-center justify-between gap-4" data-testid={`candidate-claim-${c.claim_number}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-mono text-sm font-medium truncate">{c.claim_number || c.id}</div>
                      <div className="text-xs text-gray-600 mt-0.5">
                        <span className="inline-block mr-3">DOS: {c.date_of_service || '—'}</span>
                        <span className="inline-block mr-3">Status: {c.status}</span>
                        <span className="inline-block">${(c.billed_amount || 0).toFixed?.(2) ?? c.billed_amount}</span>
                      </div>
                      <div className="text-xs mt-1 text-blue-700">{matchLabel}</div>
                    </div>
                    <Button
                      size="sm"
                      disabled={linkingClaimId !== null}
                      data-testid={`link-claim-btn-${c.claim_number}`}
                      onClick={() => linkClaim({ claim_id: c.id }, c.claim_number)}
                    >
                      {linkingClaimId === c.id ? 'Linking…' : 'Link'}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          {!candidateClaimsLoading && candidateClaims.length === 0 && (
            <div className="text-sm text-gray-500 italic" data-testid="link-existing-claim-empty">
              No candidate claims found by note ID, appointment, or client+DOS.
              If you know the claim number, paste it below.
            </div>
          )}

          <div className="border-t pt-4">
            <Label className="text-sm">Or paste a claim number</Label>
            <div className="flex gap-2 mt-1">
              <DebouncedInput
                value={manualClaimNumber}
                onChange={(e) => setManualClaimNumber(e.target.value)}
                placeholder="e.g. INT C260421001X09"
                data-testid="link-existing-claim-manual-input"
              />
              <Button
                variant="outline"
                disabled={!manualClaimNumber.trim() || linkingClaimId !== null}
                data-testid="link-existing-claim-manual-btn"
                onClick={() => linkClaim({ claim_number: manualClaimNumber.trim() }, manualClaimNumber.trim())}
              >
                Link by Number
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="link-existing-claim-cancel-btn">
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LinkExistingClaimDialog;
